import { t, translateMessage } from "../../i18n";
import { useState } from "react";
import { Eye, EyeOff, Plus } from "lucide-react";
import type { Setup, User } from "../../types";
import { useBackHandler } from "../../backNavigation";
import { newPasswordProblem } from "../../password";

type Props = {
  back: React.ReactNode;
  setup: Setup;
  currentUser: User;
  onSaveSetup: (next: Setup) => Promise<void>;
};

type PermissionKey =
  | "can_reprint"
  | "can_edit"
  | "can_cancel"
  | "can_view_failed"
  | "can_change_table_layout"
  | "can_merge_tables";

const PERMISSIONS: { key: PermissionKey; label: string }[] = [
  { key: "can_reprint", label: "Επανεκτύπωση" },
  { key: "can_edit", label: "Διόρθωση" },
  { key: "can_cancel", label: "Ακύρωση" },
  { key: "can_view_failed", label: "Αποτυχημένες εκτυπώσεις" },
  { key: "can_change_table_layout", label: "Αλλαγή διάταξης τραπεζιών" },
  { key: "can_merge_tables", label: "Συνένωση τραπεζιών" }
];

function permissionText(user: User): string {
  if (user.role === "admin") {
    return t("Διαχειριστής • όλα τα δικαιώματα");
  }
  const granted = PERMISSIONS.filter((permission) => user[permission.key] === 1).map(
    (permission) => t(permission.label)
  );
  return granted.length ? granted.join(" • ") : t("Χωρίς επιπλέον δικαιώματα");
}

export function EmployeesPage({ back, setup, currentUser, onSaveSetup }: Props) {
  const [editing, setEditing] = useState<User | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [editingPassword, setEditingPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [confirmRemove, setConfirmRemove] = useState<User[] | null>(null);
  const [note, setNote] = useState("");
  useBackHandler(Boolean(editing), () => setEditing(null));
  useBackHandler(adding, () => {
    setAdding(false);
    setNote("");
    setShowPassword(false);
  });
  useBackHandler(Boolean(confirmRemove), () => setConfirmRemove(null));

  const staff = setup.users.filter((user) => user.active && user.role !== "admin");

  const commitUsers = async (users: User[]): Promise<boolean> => {
    try {
      await onSaveSetup({ ...setup, users: users.map((user) => ({ ...user, can_view_all: 1 })) });
      return true;
    } catch (error) {
      setNote(error instanceof Error ? error.message : t("Δεν αποθηκεύτηκαν οι αλλαγές."));
      return false;
    }
  };

  const togglePermission = (key: PermissionKey): void => {
    if (!editing) {
      return;
    }
    setEditing({ ...editing, [key]: editing[key] === 1 ? 0 : 1 });
  };

  const saveEditing = async (): Promise<void> => {
    if (!editing) {
      return;
    }
    if (editingPassword && newPasswordProblem(editingPassword)) {
      setNote(newPasswordProblem(editingPassword));
      return;
    }
    const updated = editingPassword
      ? { ...editing, password: editingPassword }
      : editing;
    if (!await commitUsers(setup.users.map((user) => (user.id === editing.id ? updated : user)))) return;
    setEditing(null);
    setEditingPassword("");
    setShowPassword(false);
  };

  const addEmployee = async (): Promise<void> => {
    const username = newName.trim();
    if (!username) {
      return;
    }
    if (setup.users.some((user) => user.username.toLowerCase() === username.toLowerCase())) {
      setNote(t("Υπάρχει ήδη υπάλληλος με αυτό το όνομα."));
      return;
    }
    if (newPasswordProblem(newPassword)) {
      setNote(newPasswordProblem(newPassword));
      return;
    }
    const id = setup.users.reduce((highest, user) => Math.max(highest, user.id), 0) + 1;
    const created: User = {
      id,
      username,
      role: "staff",
      can_view_all: 1,
      can_reprint: 0,
      can_edit: 0,
      can_cancel: 0,
      can_view_failed: 0,
      can_change_table_layout: 0,
      can_merge_tables: 0,
      active: 1,
      password: newPassword,
      create_user: true
    };
    if (!await commitUsers([...setup.users, created])) return;
    setAdding(false);
    setNewName("");
    setNewPassword("");
    setNote("");
  };

  const removeEmployees = (users: User[]): void => {
    setConfirmRemove(null);
    const removing = new Set(users.map((user) => user.id));
    void commitUsers(setup.users.filter((entry) => !removing.has(entry.id)));
    setSelectedUserIds([]);
  };

  const toggleSelected = (userId: number): void => {
    setSelectedUserIds((selected) =>
      selected.includes(userId)
        ? selected.filter((id) => id !== userId)
        : [...selected, userId]
    );
  };

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Υπάλληλοι")}</h1>
          <p>{t("Λογαριασμοί και δικαιώματα")}</p>
        </div>
      </div>

      <div className="settings-groups">
        <section className="settings-group employee-create-group">
          <button type="button" className="secondary-command accent wide" onClick={() => setAdding(true)}>
            <Plus />
            <span>{t("Προσθήκη υπαλλήλου")}</span>
          </button>
          {note ? <p className="hint-text">{translateMessage(note)}</p> : null}
        </section>

        <section className="settings-group employee-list-group">
          {staff.map((user) => (
            <article className="employee-card" key={user.id}>
              <label className="employee-card-select">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => toggleSelected(user.id)}
                />
                <strong>{user.username}</strong>
              </label>
              <p className="employee-permissions">{permissionText(user)}</p>
              <button
                type="button"
                className="secondary-command wide"
                onClick={() => {
                  setEditing(user);
                  setEditingPassword("");
                  setShowPassword(false);
                }}
              >
                <span>{t("Επεξεργασία")}</span>
              </button>
            </article>
          ))}
          {!staff.length ? <p className="hint-text">{t("Δεν υπάρχουν υπάλληλοι.")}</p> : null}
        </section>

        {staff.length ? (
          <section className="settings-group employee-bulk-group">
            <button
              type="button"
              className="secondary-command danger wide"
              disabled={!selectedUserIds.length}
              onClick={() => setConfirmRemove(staff.filter((user) => selectedUserIds.includes(user.id)))}
            >
              <span>{t("Διαγραφή επιλεγμένων")}</span>
            </button>
          </section>
        ) : null}
      </div>

      {editing ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Δικαιώματα")}>
            <header className="dialog-header">
              <div>
                <h2>{editing.username}</h2>
                <p>{t("Δικαιώματα υπαλλήλου")}</p>
              </div>
            </header>
            <div className="dialog-scroll">
              {note ? <p className="form-message" role="alert">{translateMessage(note)}</p> : null}
              <div className="option-group check-grid">
                {PERMISSIONS.map((permission) => (
                  <label className="check-option" key={permission.key}>
                    <input
                      type="checkbox"
                      checked={editing[permission.key] === 1}
                      onChange={() => togglePermission(permission.key)}
                    />
                    <span>{t(permission.label)}</span>
                  </label>
                ))}
              </div>
              <label className="field">
                <span>{t("Νέος κωδικός πρόσβασης (προαιρετικό)")}</span>
                <span className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editingPassword}
                    autoComplete="new-password"
                    onChange={(event) => setEditingPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="icon-button password-toggle"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </span>
              </label>
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setEditing(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button type="button" className="primary-command" onClick={() => void saveEditing()}>
                <span>{t("Αποθήκευση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {adding ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Νέος υπάλληλος")}>
            <header className="dialog-header">
              <div>
                <h2>{t("Προσθήκη υπαλλήλου")}</h2>
                <p>{t("Τα δικαιώματα ορίζονται μετά τη δημιουργία.")}</p>
              </div>
            </header>
            <div className="dialog-scroll">
              {note ? <p className="form-message" role="alert">{translateMessage(note)}</p> : null}
              <label className="field">
                <span>{t("Όνομα χρήστη")}</span>
                <input value={newName} autoFocus onChange={(event) => setNewName(event.target.value)} />
              </label>
              <label className="field">
                <span>{t("Κωδικός πρόσβασης")}</span>
                <span className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    autoComplete="new-password"
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="icon-button password-toggle"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </span>
              </label>
            </div>
            <footer className="dialog-actions">
              <button
                type="button"
                className="secondary-command"
                onClick={() => {
                  setAdding(false);
                  setNote("");
                  setShowPassword(false);
                }}
              >
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={!newName.trim() || !newPassword}
                onClick={() => void addEmployee()}
              >
                <span>{t("Προσθήκη")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {confirmRemove ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή υπαλλήλου")}>
            <header>
              <h2>{t("Διαγραφή υπαλλήλου")}</h2>
              <p>
                {confirmRemove.length === 1
                  ? t("Να διαγραφεί ο \"{0}\";", confirmRemove[0].username)
                  : t("Να διαγραφούν οι {0} επιλεγμένοι υπάλληλοι;", confirmRemove.length)}
              </p>
            </header>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setConfirmRemove(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                onClick={() => removeEmployees(confirmRemove)}
              >
                <span>{t("Διαγραφή")}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
